// The grappling vocabulary layer for search. A clip is titled whatever its
// creator called it, so the words a person actually types — "honey hole",
// "411", "outside sankaku" — are usually nowhere in the catalog. Each entry
// here is one thing under all the names it goes by: type any of them and the
// search widens to the positions and the wording that thing really uses.
//
// THE RULE for a term: it must be another NAME FOR THE SAME THING. Never a
// piece of it and never a family containing it. Rubber guard is played in
// closed guard but it is not closed guard, so listing it there would turn one
// search into two dozen unrelated clips. Anything narrower gets its own entry
// (see Rubber Guard, Deep Half, Knee Shield below), which matches on wording
// alone and quietly disappears from the How To list while the catalog has no
// clips for it. The one deliberate exception is Front Headlock, a family that
// widens to its members, which is the direction that helps rather than hurts.
//
// Positions are already matched on their label (AppShell's catLabel check), so
// an entry only earns its place when it carries a word that label does not:
// "mount" needs nothing here, "back mount" does.
//
// This is a hand-kept list, not data on the techniques. The per-technique
// `aliases` / `japaneseName` fields still match on their own; this sits
// alongside them and needs no edit per clip.

/** One idea, the words for it, and where it lives in the catalog. */
export interface LingoTerm {
  /** The everyday name, for the How To list. */
  label: string;
  /** Everything a person might type for it. Include the label's own wording:
   *  these are what the typed query is checked against. */
  terms: string[];
  /** Category ids this names, when the term is a position. */
  positions?: string[];
  /** Wording to look for in a technique's name or its own aliases, when the
      term is a move rather than a position. */
  names?: string[];
}

/** Punctuation and spacing are noise here: "50/50", "50-50" and "fifty fifty"
 *  are one search, so both sides are stripped to letters and digits. */
export function normalizeLingo(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Below this a query is too short to mean anything — two letters would drag
 *  in half the list. "411" is the shortest real term. */
const MIN_QUERY = 3;

export const LINGO: LingoTerm[] = [
  // ── Leg entanglements: the position everyone names differently ──
  {
    label: 'Saddle',
    terms: ['saddle', 'honey hole', '411', 'inside sankaku', 'inside senkaku', 'inside ashi'],
    names: ['saddle'],
  },
  {
    label: 'Single Leg X',
    terms: ['single leg x', 'slx', 'outside sankaku', 'outside senkaku', 'outside ashi'],
    positions: ['slx'],
    names: ['single leg x', 'slx'],
  },
  {
    // The family the three above belong to, so the general term reaches all of
    // them. Kept separate from Single Leg X, which is only one of them.
    label: 'Leg Entanglement',
    terms: ['leg entanglement', 'ashi garami', 'ashi', 'leg lock position'],
    positions: ['slx', 'fifty-fifty'],
    names: ['saddle', 'ashi garami'],
  },
  {
    label: '50/50',
    terms: ['50/50', 'fifty fifty', 'double outside ashi'],
    positions: ['fifty-fifty'],
    names: ['50/50'],
  },
  {
    label: 'X Guard',
    terms: ['x guard', 'full x'],
    positions: ['x-guard'],
    names: ['x guard'],
  },
  // ── Open guards ──
  {
    label: 'De la Riva',
    terms: ['de la riva', 'dlr'],
    positions: ['dlr'],
    names: ['de la riva', 'dlr'],
  },
  {
    label: 'Reverse De la Riva',
    terms: ['reverse de la riva', 'rdlr'],
    positions: ['rdlr'],
    names: ['reverse de la riva', 'rdlr'],
  },
  {
    label: 'Spider / Lasso',
    terms: ['spider guard', 'lasso guard'],
    positions: ['spider-lasso'],
    names: ['spider', 'lasso'],
  },
  {
    label: 'Butterfly Guard',
    terms: ['butterfly guard', 'hooks guard'],
    positions: ['butterfly-guard'],
    names: ['butterfly'],
  },
  {
    label: 'Seated Guard',
    terms: ['seated guard', 'sit up guard', 'butt scoot'],
    positions: ['seated-guard'],
    names: ['seated guard'],
  },
  {
    label: 'Closed Guard',
    terms: ['closed guard', 'full guard'],
    positions: ['closed-guard'],
    names: ['closed guard'],
  },
  {
    label: 'Worm Guard',
    terms: ['worm guard', 'lapel guard'],
    positions: ['worm-guard'],
    names: ['worm guard', 'lapel'],
  },
  {
    label: 'Berimbolo',
    terms: ['berimbolo', 'bolo'],
    positions: ['berimbolo'],
    names: ['berimbolo', 'bolo'],
  },
  {
    label: 'Guard Retention',
    terms: ['guard retention', 'guard recovery', 'recover guard'],
    positions: ['retention'],
    names: ['retention', 'recovery'],
  },
  // ── Guards inside a guard: their own entries, never terms on the position
  //    they are played from. ──
  {
    label: 'Rubber Guard',
    terms: ['rubber guard', 'mission control', 'new york'],
    names: ['rubber guard', 'mission control'],
  },
  {
    label: 'Deep Half',
    terms: ['deep half', 'deep half guard'],
    names: ['deep half'],
  },
  {
    label: 'Knee Shield',
    terms: ['knee shield', 'z guard', 'zed guard'],
    names: ['knee shield', 'z guard'],
  },
  {
    label: 'Shin to Shin',
    terms: ['shin to shin', 'shin on shin'],
    names: ['shin to shin'],
  },
  // ── Top and back ──
  {
    label: 'Back Control',
    terms: ['back control', 'back mount', 'taking the back'],
    positions: ['back-control'],
    names: ['back take', 'back control'],
  },
  {
    label: 'Side Control',
    terms: ['side control', 'side mount', 'cross body', '100 kilos', 'cem quilos'],
    positions: ['side-control'],
    names: ['side control'],
  },
  {
    label: 'Scarf Hold',
    terms: ['scarf hold', 'kesa gatame'],
    names: ['kesa gatame', 'scarf hold'],
  },
  {
    label: 'Knee on Belly',
    terms: ['knee on belly', 'kob', 'knee ride', 'knee mount'],
    names: ['knee on belly', 'knee ride'],
  },
  {
    label: 'Standing',
    terms: ['standing', 'on the feet', 'takedowns'],
    positions: ['standing'],
    names: ['takedown', 'throw'],
  },
  // ── Submissions, under every name they carry ──
  {
    label: 'Rear Naked Choke',
    terms: ['rear naked choke', 'rnc', 'mata leao', 'hadaka jime'],
    names: ['rear naked', 'rnc', 'hadaka jime'],
  },
  {
    label: 'Armbar',
    terms: ['armbar', 'arm bar', 'juji gatame', 'juji', 'straight arm lock'],
    names: ['armbar', 'arm bar', 'juji gatame'],
  },
  {
    label: 'Triangle Choke',
    terms: ['triangle', 'sankaku jime', 'sankaku', 'senkaku'],
    names: ['triangle', 'sankaku'],
  },
  {
    label: 'Kimura',
    terms: ['kimura', 'reverse arm lock', 'double wrist lock', 'ude garami'],
    names: ['kimura', 'reverse arm lock'],
  },
  {
    label: 'Omoplata',
    terms: ['omoplata', 'shoulder lock'],
    names: ['omoplata', 'shoulder lock'],
  },
  {
    label: 'Guillotine',
    terms: ['guillotine', 'mae hadaka jime'],
    names: ['guillotine', 'mae hadaka jime'],
  },
  {
    // A family widening to its members: the position is where all three are
    // finished from, so the clips it reaches are the right ones.
    label: 'Front Headlock',
    terms: ['front headlock', 'front head lock'],
    names: ['front headlock', 'guillotine', 'darce', 'anaconda'],
  },
  {
    label: "D'Arce / Anaconda",
    terms: ['darce', "d'arce", 'anaconda', 'brabo'],
    names: ['darce', "d'arce", 'anaconda', 'brabo'],
  },
  {
    label: 'Heel Hook',
    terms: ['heel hook', 'heel'],
    names: ['heel hook'],
  },
  {
    label: 'Wrist Lock',
    terms: ['wrist lock', 'kote gaeshi'],
    names: ['wrist lock', 'wrist crush', 'kote gaeshi'],
  },
  {
    label: 'Wrestle Up',
    terms: ['wrestle up', 'get up', 'stand up from guard'],
    names: ['wrestle up', 'get up'],
  },
];

/** The entries whose vocabulary carries the typed query — a query anywhere
 *  inside one of the terms counts, so "honey" finds the honey hole. */
export function lingoFor(query: string): LingoTerm[] {
  const q = normalizeLingo(query);
  if (q.length < MIN_QUERY) return [];
  return LINGO.filter((e) => e.terms.some((t) => normalizeLingo(t).includes(q)));
}

/** Is this technique one of the ones the entry is about? Its position, or the
 *  entry's wording in the technique's own name / other names. */
export function matchesLingo(
  entry: LingoTerm,
  tech: { name: string; categoryId: string; japaneseName?: string; aliases?: string[] },
): boolean {
  if (entry.positions?.includes(tech.categoryId)) return true;
  if (!entry.names) return false;
  const hay = normalizeLingo([tech.name, tech.japaneseName, ...(tech.aliases ?? [])].filter(Boolean).join(' '));
  return entry.names.some((n) => hay.includes(normalizeLingo(n)));
}
